import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, PageTitle, StatusBadge, fmtMoney, fmtDate, fmtDateTime, Th, Td, Modal } from "@/admin/ui";

function CustomerDetail({ email, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/customers/${encodeURIComponent(email)}`).then((r) => setData(r.data)).catch(() => {});
  }, [email]);

  return (
    <Modal open onClose={onClose} wide>
      <div data-testid="customer-detail">
        <h2 className="font-anton uppercase text-2xl mb-1">{data?.profile?.name || data?.orders?.[0]?.customer_name || email}</h2>
        <p className="font-mono text-xs text-graphite mb-6">{email}{data?.profile ? " · registered account" : " · guest checkout"}</p>
        {!data ? <p className="font-mono text-xs text-graphite">Loading…</p> : (
          <div className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite">Order history ({data.orders.length})</p>
            {data.orders.length === 0 && <p className="text-graphite text-sm">No orders yet.</p>}
            {data.orders.map((o) => (
              <div key={o.session_id} className="border border-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs text-signal">{o.order_number || `…${o.session_id.slice(-8)}`}</span>
                  <span className="font-mono text-[10px] text-graphite">{fmtDateTime(o.created_at)}</span>
                  <StatusBadge status={o.fulfillment_status || "new"} />
                  <span className="font-mono text-xs">{fmtMoney(o.amount_total ?? o.amount)}</span>
                </div>
                <p className="font-mono text-[10px] text-graphite mt-2">
                  {(o.items || []).map((i) => `${i.quantity}× ${i.name}${i.size ? ` (${i.size}${i.color ? `/${i.color}` : ""})` : ""}`).join(" · ")}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-customers-page">
      <PageTitle title="Customers" sub={`${customers.length} customers & registered users.`} />
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="border-b border-border">
            <tr><Th>Customer</Th><Th>Orders</Th><Th>Total spent</Th><Th>Last order</Th><Th>Account</Th></tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr><Td colSpan={5} className="text-graphite font-mono text-xs py-8 text-center">No customers yet.</Td></tr>
            )}
            {customers.map((c) => (
              <tr key={c.email} onClick={() => setSelected(c.email)} data-testid={`customer-row-${c.email}`}
                className="border-b border-border/40 hover:bg-chalk/5 cursor-pointer transition-colors">
                <Td>
                  <p className="text-sm">{c.name || "—"}</p>
                  <p className="font-mono text-[10px] text-graphite">{c.email}</p>
                </Td>
                <Td className="font-mono text-xs">{c.orders_count}</Td>
                <Td className="font-mono text-xs text-signal">{fmtMoney(c.total_spent)}</Td>
                <Td className="font-mono text-xs text-graphite">{fmtDate(c.last_order)}</Td>
                <Td className="font-mono text-[9px] uppercase tracking-wider text-graphite">{c.registered ? "Registered" : "Guest"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {selected && <CustomerDetail email={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
